import { useCallback, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

/** RISP countries in the North Africa region (admin entry dropdown). */
export const NORTH_AFRICA_RISP_COUNTRIES = [
  'Algeria',
  'Libya',
  'Mauritania',
  'Morocco',
  'Tunisia',
] as const;

export type NorthAfricaRispCountry = (typeof NORTH_AFRICA_RISP_COUNTRIES)[number];

const STORAGE_KEY = 'risp.adminWorkingCountry';

function readStoredAdminCountry(): string {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || '';
    if ((NORTH_AFRICA_RISP_COUNTRIES as readonly string[]).includes(raw)) return raw;
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * Country used for RISP data entry/load.
 * Admins pick a North Africa country; everyone else uses their profile country.
 */
export function useRispWorkingCountry() {
  const { user } = useAuthStore();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [adminCountry, setAdminCountryState] = useState<string>(readStoredAdminCountry);

  const setAdminCountry = useCallback((country: string) => {
    setAdminCountryState(country);
    try {
      if (country) sessionStorage.setItem(STORAGE_KEY, country);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const workingCountry = isAdmin ? adminCountry : user?.country || '';

  return {
    isAdmin,
    workingCountry,
    adminCountry,
    setAdminCountry,
    northAfricaCountries: NORTH_AFRICA_RISP_COUNTRIES as readonly string[],
  };
}
